import React, { useState, useEffect } from 'react';
import AppCard from '../components/AppCard';
import AppFormModal from '../components/AppFormModal';
import appService from '../services/appService';
import authService from '../services/authService';

const Dashboard = ({ user, onLogout }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [error, setError] = useState('');

  const isAdmin = authService.isAdmin();

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const apps = await appService.getApplications();
      setApplications(apps);
    } catch (error) {
      setError('Erreur lors du chargement des applications');
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddApp = () => {
    setEditingApp(null);
    setShowAddModal(true);
  };

  const handleEditApp = (app) => {
    setEditingApp(app);
    setShowAddModal(true);
  };

  const handleSaveApp = async (appData) => {
    try {
      if (editingApp) {
        await appService.updateApplication(editingApp.id, appData);
      } else {
        await appService.addApplication(appData);
      }
      
      setShowAddModal(false);
      setEditingApp(null);
      loadApplications();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteApp = async (appId) => {
    try {
      await appService.deleteApplication(appId);
      loadApplications();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      await authService.logout();
      onLogout();
    }
  };

  const activeApps = applications.filter(app => app.active);
  const inactiveApps = applications.filter(app => !app.active);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">🏰</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Bastion Admin</h1>
              </div>
              <div className="hidden sm:block">
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                  {activeApps.length} applications actives
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {isAdmin && (
                <button
                  onClick={handleAddApp}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Ajouter une app</span>
                </button>
              )}
              
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-600">
                  Connecté en tant que <span className="font-medium">{user.username}</span>
                  {user.role === 'admin' && (
                    <span className="ml-1 bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded">Admin</span>
                  )}
                </div>
                <button
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-gray-100 transition-colors"
                  title="Se déconnecter"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError('')}
                  className="text-red-400 hover:text-red-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Bienvenue, {user.username} ! 👋
          </h2>
          <p className="text-gray-600">
            Accédez à vos outils d'administration en un clic. Toutes vos applications sont centralisées ici.
          </p>
        </div>

        {/* Applications actives */}
        {activeApps.length > 0 ? (
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Applications disponibles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeApps.map(app => (
                <AppCard
                  key={app.id}
                  app={app}
                  onEdit={handleEditApp}
                  onDelete={handleDeleteApp}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune application disponible</h3>
              <p className="text-gray-500 mb-4">
                {isAdmin ? 'Commencez par ajouter votre première application.' : 'Contactez votre administrateur pour ajouter des applications.'}
              </p>
              {isAdmin && (
                <button
                  onClick={handleAddApp}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
                >
                  Ajouter une application
                </button>
              )}
            </div>
          </div>
        )}

        {/* Applications inactives (admin seulement) */}
        {isAdmin && inactiveApps.length > 0 && (
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              Applications inactives
              <span className="ml-2 bg-gray-100 text-gray-600 text-sm px-2 py-1 rounded">
                {inactiveApps.length}
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {inactiveApps.map(app => (
                <div key={app.id} className="opacity-60">
                  <AppCard
                    app={app}
                    onEdit={handleEditApp}
                    onDelete={handleDeleteApp}
                    isAdmin={isAdmin}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Modal pour ajouter/modifier une application */}
      <AppFormModal
        app={editingApp}
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingApp(null);
        }}
        onSave={handleSaveApp}
      />
    </div>
  );
};

export default Dashboard;
